import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Trophy, CheckCircle2, XCircle, Loader2, Sparkles } from 'lucide-react';

const WeightedAssessmentResultsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wa, setWa] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.waGet(id);
        setWa(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-emerald-600" size={32}/></div>;
  if (!wa) return null;

  if (wa.status !== 'submitted' || !wa.result) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-3">
        <p className="text-slate-600">This assessment has not been submitted yet.</p>
        <Button onClick={() => navigate(`/weighted-assessments/${id}`)} className="bg-emerald-600 text-white rounded-full">Continue attempt</Button>
      </div>
    );
  }

  const r = wa.result;
  const pctColor = r.percentage >= 80 ? 'text-emerald-700' : r.percentage >= 60 ? 'text-amber-700' : 'text-rose-700';

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <button
          onClick={() => navigate('/weighted-assessments')}
          className="flex items-center gap-1.5 text-slate-600 hover:text-emerald-700 font-semibold text-sm mb-6"
          data-testid="wa-results-back"
        >
          <ArrowLeft size={16} /> Back to Weighted Assessments
        </button>

        {/* Score hero */}
        <Card className="p-6 md:p-8 rounded-3xl border-2 border-emerald-500 shadow-xl mb-6 bg-gradient-to-br from-emerald-50 to-white">
          <div className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full mb-3">
            <Trophy size={12}/> Weighted Assessment · P6
          </div>
          <div className="flex flex-wrap items-end gap-6 mb-2">
            <div>
              <div className="text-xs font-black uppercase text-slate-500">Total score</div>
              <div className="text-6xl font-heading font-black text-emerald-700">
                {r.total_scored}<span className="text-slate-400 text-3xl">/{r.total_marks}</span>
              </div>
            </div>
            <div>
              <div className="text-xs font-black uppercase text-slate-500">Percentage</div>
              <div className={`text-4xl font-heading font-black ${pctColor}`}>{r.percentage}%</div>
            </div>
          </div>
          <p className="text-slate-600 text-sm mt-2">
            Completed {new Date(wa.submitted_at).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' })}
          </p>
        </Card>

        {/* Section + topic breakdown */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {r.sections.map((sec) => (
            <Card key={sec.section} className="p-5 rounded-2xl border-2 border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[11px] font-black uppercase text-emerald-700 tracking-wider">Section {sec.section}</div>
                  <h3 className="font-heading font-black text-slate-900">{sec.label}</h3>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-heading font-black text-slate-800">{sec.scored}<span className="text-slate-400 text-lg">/{sec.marks}</span></div>
                </div>
              </div>
              <div className="space-y-2">
                {sec.topics.map((t) => {
                  const pct = t.marks ? (t.scored / t.marks) * 100 : 0;
                  const color = pct >= 80 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-rose-500';
                  return (
                    <div key={t.topic}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-semibold text-slate-700">{t.label}</span>
                        <span className="font-bold text-slate-800">{t.scored}/{t.marks} · {pct.toFixed(0)}%</span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.7 }} className={`h-full ${color}`}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>

        {/* AI feedback */}
        {r.ai_feedback && (
          <Card className="p-6 rounded-3xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white mb-6">
            <div className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full mb-3">
              <Sparkles size={12}/> Prof.Scaly's feedback
            </div>
            <div className="text-slate-800 leading-relaxed whitespace-pre-wrap text-sm">
              {r.ai_feedback}
            </div>
          </Card>
        )}

        {/* Detailed per-topic review */}
        {r.sections.map((sec) => (
          <div key={`review-${sec.section}`} className="mb-8">
            <h2 className="text-xl font-heading font-black text-slate-900 mb-3">Section {sec.section} · Question review</h2>
            {sec.topics.map((t) => (
              <Card key={t.topic} className="p-5 mb-3 rounded-2xl border-2 border-slate-200">
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-100">
                  <h3 className="font-heading font-black text-slate-900">{t.label}</h3>
                  <span className="text-xs font-bold text-slate-700">{t.scored}/{t.marks}</span>
                </div>
                <div className="space-y-2">
                  {t.per_question.map((pq) => (
                    <div key={pq.id} className={`p-3 rounded-xl border ${pq.marks_awarded === pq.marks_possible ? 'border-emerald-200 bg-emerald-50/40' : pq.marks_awarded > 0 ? 'border-amber-200 bg-amber-50/40' : 'border-rose-200 bg-rose-50/40'}`}>
                      <div className="flex items-start gap-2 text-xs">
                        {pq.marks_awarded === pq.marks_possible ? <CheckCircle2 size={14} className="text-emerald-600 mt-0.5"/> : pq.marks_awarded > 0 ? <CheckCircle2 size={14} className="text-amber-600 mt-0.5"/> : <XCircle size={14} className="text-rose-500 mt-0.5"/>}
                        <div className="flex-1 space-y-1">
                          <div>
                            <span className="font-black text-slate-500 mr-2">Q{pq.n || '?'}</span>
                            <span className="font-semibold text-slate-800">Your answer:</span>{' '}
                            <span className="italic">{pq.user_answer || <em className="text-slate-400">— blank —</em>}</span>
                          </div>
                          {pq.correct && (
                            <div>
                              <span className="font-semibold text-slate-800">Correct:</span>{' '}
                              <span className="text-emerald-700 font-bold">{pq.correct}</span>
                            </div>
                          )}
                          {pq.sample_answer && (
                            <div className="text-slate-600">
                              <span className="font-semibold">Sample answer:</span> {pq.sample_answer}
                            </div>
                          )}
                          {pq.feedback && (
                            <div className="text-slate-600 italic">{pq.feedback}</div>
                          )}
                        </div>
                        <div className="text-xs font-black text-slate-700 whitespace-nowrap">{pq.marks_awarded}/{pq.marks_possible}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        ))}

        <div className="text-center py-6">
          <Button onClick={() => navigate('/weighted-assessments')} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl px-8 py-6 font-bold">
            Back to Weighted Assessments
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WeightedAssessmentResultsPage;
